/**
 * Main application entry point
 * Coordinates Web Worker, UI, and visualization
 */

import type { WorkerMessage, WorkerResponse, Sample, SimConfig } from './sim/types';
import { SimulationScene } from './viz/scene';
import { SimulationPlots } from './viz/plot';
import { SimulationControls } from './ui/controls';

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

/**
 * Main application class
 */
class AniTopApp {
  private worker: Worker;
  private scene: SimulationScene;
  private plots: SimulationPlots;
  private controls: SimulationControls;

  private lastFrameTime = 0;
  private fps = 0;
  private frameCount = 0;
  private fpsUpdateTime = 0;

  private isInitialized = false;

  constructor() {
    // Initialize Web Worker
    this.worker = new Worker(new URL('./sim/worker.ts', import.meta.url), {
      type: 'module'
    });

    this.worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      this.handleWorkerMessage(e.data);
    };

    this.worker.onerror = (error) => {
      console.error('Worker error:', error);
      alert('Simulation worker error. Check console for details.');
    };

    // Initialize visualization
    const canvas = document.getElementById('three-canvas') as HTMLCanvasElement;
    if (!canvas) {
      throw new Error('Canvas element not found');
    }
    this.scene = new SimulationScene(canvas);

    // Initialize plots
    this.plots = new SimulationPlots('omega-plot', 'energy-plot');

    // Initialize controls
    this.controls = new SimulationControls('control-panel', {
      onConfigChange: (config) => this.initializeSimulation(config),
      onStart: () => this.start(),
      onPause: () => this.pause(),
      onReset: () => this.reset(),
      onExport: (format) => this.exportData(format)
    });

    // Handle visualization toggles
    window.addEventListener('viz-toggle', ((e: CustomEvent) => {
      const { type, value } = e.detail;
      switch (type) {
        case 'ellipsoid':
          this.scene.setShowEllipsoid(value);
          this.scene.setShowTopMesh(!value);
          break;
        case 'bodyAxes':
          this.scene.setShowBodyAxes(value);
          break;
        case 'worldAxes':
          this.scene.setShowWorldAxes(value);
          break;
        case 'angularMomentum':
          this.scene.setShowAngularMomentum(value);
          break;
        case 'trail':
          this.scene.setShowTrail(value);
          break;
      }
    }) as EventListener);

    // Handle window resize
    window.addEventListener('resize', () => {
      this.plots.resize();
    });

    // Hook up canvas control buttons
    const startPauseBtn = document.getElementById('start-pause-btn');
    const resetBtn = document.getElementById('reset-btn');

    if (startPauseBtn) {
      startPauseBtn.addEventListener('click', () => {
        if (startPauseBtn.textContent === '▶' || startPauseBtn.textContent === '▶︎') {
          // Validate inputs before starting
          if (!this.controls.canStartSimulation()) {
            return; // Don't start if validation fails
          }
          this.start();
          startPauseBtn.textContent = '⏸';
        } else {
          this.pause();
          startPauseBtn.textContent = '▶';
        }
      });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        // First pause the simulation
        this.pause();
        // Then reset to initial state
        this.reset();
        // Update button to show play symbol
        if (startPauseBtn) {
          startPauseBtn.textContent = '▶';
        }
      });
    }

    // Initialize with default configuration
    const config = this.controls.getConfig();
    this.initializeSimulation(config);

    // Start animation loop
    this.animate();
  }

  private initializeSimulation(config: SimConfig): void {
    const message: WorkerMessage = {
      type: 'init',
      payload: config
    };

    this.worker.postMessage(message);
    this.plots.clear();
    this.scene.clearTrail();

    // Update ellipsoid shape immediately with new inertia values
    this.scene.updateInertia(config.inertia);
  }

  private handleWorkerMessage(response: WorkerResponse): void {
    switch (response.type) {
      case 'ready':
        this.isInitialized = true;
        console.log('Simulation initialized');
        break;

      case 'sample':
        this.updateVisualization(response.payload);
        break;

      case 'export':
        if (response.payload.rowCount > 1000) {
          (window as any).showNotification?.('Data export in progress, may take a few seconds');
        }
        this.downloadFile(response.payload.data, response.payload.filename);
        console.log(`Exported ${response.payload.format.toUpperCase()} data`);
        break;

      case 'error':
        console.error('Simulation error:', response.payload.message);
        alert('Simulation error: ' + response.payload.message);
        break;
    }
  }

  private updateVisualization(sample: Sample): void {
    // Update 3D scene
    const config = this.controls.getConfig();
    this.scene.update(sample.R, sample.omega, sample.L, config.inertia);

    // Update plots
    this.plots.addSample(sample);

    // Update canvas overlay
    const fpsDisplay = document.getElementById('fps-display');
    const timeDisplay = document.getElementById('time-display');
    const energyDisplay = document.getElementById('energy-display');

    if (fpsDisplay) fpsDisplay.textContent = this.fps.toFixed(0);
    if (timeDisplay) timeDisplay.textContent = sample.t.toFixed(3);
    if (energyDisplay) energyDisplay.textContent = sample.energy.toFixed(6);
  }

  private animate(): void {
    requestAnimationFrame(() => this.animate());

    const now = performance.now();
    const dtRender = (now - this.lastFrameTime) / 1000; // Convert to seconds
    this.lastFrameTime = now;

    // Calculate FPS
    this.frameCount++;
    if (now - this.fpsUpdateTime > 1000) {
      this.fps = this.frameCount;
      this.frameCount = 0;
      this.fpsUpdateTime = now;
    }

    // Send tick to worker
    if (this.isInitialized) {
      const message: WorkerMessage = {
        type: 'tick',
        payload: { dtRender }
      };
      this.worker.postMessage(message);
    }

    // Render scene
    this.scene.render();
  }

  private start(): void {
    const message: WorkerMessage = { type: 'start' };
    this.worker.postMessage(message);
  }

  private pause(): void {
    const message: WorkerMessage = { type: 'pause' };
    this.worker.postMessage(message);
  }

  private reset(): void {
    const message: WorkerMessage = {
      type: 'reset',
      payload: {}
    };
    this.worker.postMessage(message);
    this.plots.clear();
    this.scene.clearTrail();
  }

  private exportData(format: 'csv' | 'json'): void {
    const message: WorkerMessage = {
      type: 'export',
      payload: { format }
    };
    this.worker.postMessage(message);
  }

  private downloadFile(data: string, filename: string): void {
    const blob = new Blob([data], {
      type: filename.endsWith('.csv') ? 'text/csv' : 'application/json'
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
  }
}

/**
 * Initialize theme toggle
 */
function initThemeToggle() {
  const themeToggle = document.getElementById('theme-toggle');
  const root = document.documentElement;

  // Load saved theme or use system preference
  const savedTheme = localStorage.getItem('theme');
  if (savedTheme) {
    root.setAttribute('data-theme', savedTheme);
    updateThemeIcon(savedTheme);
  } else {
    // Check system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const theme = prefersDark ? 'dark' : 'light';
    root.setAttribute('data-theme', theme);
    updateThemeIcon(theme);
  }

  // Toggle theme on click
  themeToggle?.addEventListener('click', () => {
    const currentTheme = root.getAttribute('data-theme');
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    root.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
    updateThemeIcon(newTheme);

    // Dispatch custom event for components to update
    window.dispatchEvent(new CustomEvent('theme-change'));
  });

  function updateThemeIcon(theme: string) {
    if (themeToggle) {
      themeToggle.textContent = theme === 'dark' ? '🌙' : '☀️';
    }
  }
}

/**
 * Initialize resizable panels
 */
function initResizers() {
  // Horizontal resizer (controls width)
  const controlsResizer = document.getElementById('controls-resizer');
  const controls = document.getElementById('controls');

  if (controlsResizer && controls) {
    let isResizing = false;

    controlsResizer.addEventListener('mousedown', (e) => {
      isResizing = true;
      controlsResizer.classList.add('resizing');
      document.body.style.cursor = 'ew-resize';
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      if (newWidth >= 250 && newWidth <= 600) {
        controls.style.width = `${newWidth}px`;
      }
    });

    document.addEventListener('mouseup', () => {
      if (isResizing) {
        isResizing = false;
        controlsResizer.classList.remove('resizing');
        document.body.style.cursor = '';
      }
    });
  }

  // Vertical resizer (canvas/plots height)
  const canvasPlotsResizer = document.getElementById('canvas-plots-resizer');
  const canvasContainer = document.getElementById('canvas-container');
  const plotsContainer = document.getElementById('plots-container');
  const mainContent = document.getElementById('main-content');

  if (canvasPlotsResizer && canvasContainer && plotsContainer && mainContent) {
    let isResizing = false;

    canvasPlotsResizer.addEventListener('mousedown', (e) => {
      isResizing = true;
      canvasPlotsResizer.classList.add('resizing');
      document.body.style.cursor = 'ns-resize';
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      if (!isResizing) return;

      const mainRect = mainContent.getBoundingClientRect();
      const relativeY = e.clientY - mainRect.top;
      const totalHeight = mainRect.height - 4; // Subtract resizer height

      const canvasFlex = relativeY / totalHeight;
      const plotsFlex = 1 - canvasFlex;

      if (relativeY >= 300 && (totalHeight - relativeY) >= 200) {
        canvasContainer.style.flex = `${canvasFlex * 3}`;
        plotsContainer.style.flex = `${plotsFlex * 3}`;
      }
    });

    document.addEventListener('mouseup', () => {
      if (isResizing) {
        isResizing = false;
        canvasPlotsResizer.classList.remove('resizing');
        document.body.style.cursor = '';
        // Trigger resize event for plots
        window.dispatchEvent(new Event('resize'));
      }
    });
  }
}

/**
 * Initialize notification banner
 */
function initNotificationBanner() {
  let notificationTimeout: number | undefined;

  (window as any).showNotification = (message: string) => {
    const banner = document.getElementById('notification-banner');
    if (!banner) return;

    banner.textContent = message;
    banner.classList.add('show');

    if (notificationTimeout) {
      clearTimeout(notificationTimeout);
    }

    notificationTimeout = window.setTimeout(() => {
      banner.classList.remove('show');
    }, 2000);
  };
}

/**
 * Initialize onboarding overlay
 */
function initOnboarding(forceShow: boolean = false) {
  // Check if user has completed onboarding
  const hasCompletedOnboarding = localStorage.getItem('anitop-onboarding-completed');
  if (!forceShow && hasCompletedOnboarding === 'true') {
    return; // Skip onboarding
  }

  const overlay = document.getElementById('onboarding-overlay');
  const spotlight = document.getElementById('onboarding-spotlight');
  const arrow = document.getElementById('onboarding-arrow');
  const tooltip = document.getElementById('onboarding-tooltip');
  const skipBtn = document.getElementById('onboarding-skip');

  if (!overlay || !spotlight || !arrow || !tooltip || !skipBtn) {
    return;
  }

  let currentStep = 0;

  const steps = [
    {
      target: '#top-type',
      arrowDirection: 'left',
      title: '1. Choose a Simulator',
      message: 'Start by selecting which type of rigid body you want to simulate.',
      onComplete: () => {
        // Wait for user to interact with the top type selector
        const topTypeSelect = document.getElementById('top-type');
        if (topTypeSelect) {
          // Detect when user opens and closes the dropdown
          let hasOpened = false;

          // Mark as opened when they focus on it
          topTypeSelect.addEventListener('focus', () => {
            hasOpened = true;
          }, { once: true });

          // Advance when they close it after opening
          topTypeSelect.addEventListener('blur', () => {
            if (hasOpened) {
              nextStep();
            }
          }, { once: true });

          // Or advance immediately if they change the value
          topTypeSelect.addEventListener('change', () => {
            nextStep();
          }, { once: true });
        }
      }
    },
    {
      target: '#preset',
      arrowDirection: 'left',
      title: '2. Select a Preset',
      message: 'Pick a preset configuration to see interesting physics behavior.',
      onComplete: () => {
        // Wait for user to interact with the preset selector
        const presetSelect = document.getElementById('preset');
        if (presetSelect) {
          // Detect when user opens and closes the dropdown
          let hasOpened = false;

          // Mark as opened when they focus on it
          presetSelect.addEventListener('focus', () => {
            hasOpened = true;
          }, { once: true });

          // Advance when they close it after opening
          presetSelect.addEventListener('blur', () => {
            if (hasOpened) {
              nextStep();
            }
          }, { once: true });

          // Or advance immediately if they change the value
          presetSelect.addEventListener('change', () => {
            nextStep();
          }, { once: true });
        }
      }
    },
    {
      target: '#start-pause-btn',
      arrowDirection: 'down',
      title: '3. Start the Simulation',
      message: 'Click the play button to begin!',
      onComplete: () => {
        // Wait for user to click start button
        const startBtn = document.getElementById('start-pause-btn');
        if (startBtn) {
          startBtn.addEventListener('click', () => {
            completeOnboarding();
          }, { once: true });
        }
      }
    }
  ];

  function showStep(stepIndex: number) {
    if (stepIndex >= steps.length) {
      completeOnboarding();
      return;
    }

    const step = steps[stepIndex];
    const targetElement = document.querySelector(step.target) as HTMLElement;

    if (!targetElement) {
      console.warn('Onboarding target not found:', step.target);
      nextStep();
      return;
    }

    // Position spotlight
    const rect = targetElement.getBoundingClientRect();
    spotlight!.style.left = `${rect.left - 10}px`;
    spotlight!.style.top = `${rect.top - 10}px`;
    spotlight!.style.width = `${rect.width + 20}px`;
    spotlight!.style.height = `${rect.height + 20}px`;

    // Position arrow
    arrow!.className = `onboarding-arrow ${step.arrowDirection}`;
    if (step.arrowDirection === 'left') {
      arrow!.textContent = '←';
      arrow!.style.left = `${rect.right + 30}px`;
      arrow!.style.top = `${rect.top + rect.height / 2 - 24}px`;
    } else if (step.arrowDirection === 'down') {
      arrow!.textContent = '↓';
      arrow!.style.left = `${rect.left + rect.width / 2 - 24}px`;
      arrow!.style.top = `${rect.top - 60}px`;
    }

    // Update tooltip content - skip button moved to bottom
    const contentHTML = `
      <h3>${step.title}</h3>
      <p>${step.message}</p>
      <button class="onboarding-skip" id="onboarding-skip">Skip Tutorial →</button>
    `;
    tooltip!.innerHTML = contentHTML;

    // Tooltip stays fixed in center - no need to reposition

    // Re-attach skip button handler after innerHTML update
    const newSkipBtn = document.getElementById('onboarding-skip');
    if (newSkipBtn) {
      newSkipBtn.addEventListener('click', completeOnboarding);
    }

    // Set up completion handler
    if (step.onComplete) {
      step.onComplete();
    }
  }

  function nextStep() {
    currentStep++;
    showStep(currentStep);
  }

  function completeOnboarding() {
    overlay!.classList.remove('show');
    localStorage.setItem('anitop-onboarding-completed', 'true');
  }

  // Show overlay and start first step
  const delay = forceShow ? 100 : 1500; // Shorter delay if manually triggered
  setTimeout(() => {
    overlay.classList.add('show');
    showStep(0);
  }, delay);
}

/**
 * Browser entry point
 */
function initBrowser() {
  console.log('%cAniTop Web Simulator', 'font-size: 20px; font-weight: bold; color: #00ff88');
  console.log('%cRigid body physics simulation with SO(3) integration', 'color: #888');
  console.log('');

  // Initialize notification banner
  initNotificationBanner();

  // Initialize theme toggle
  initThemeToggle();

  // Initialize resizers
  initResizers();

  // Hide loading screen
  setTimeout(() => {
    const loadingScreen = document.getElementById('loading-screen');
    if (loadingScreen) {
      loadingScreen.classList.add('hidden');
      setTimeout(() => {
        document.body.classList.add('loaded');
      }, 500);
    }
  }, 1000);

  // Initialize onboarding
  initOnboarding();

  // Add restart tutorial button handler
  const restartTutorialBtn = document.getElementById('restart-tutorial-btn');
  if (restartTutorialBtn) {
    restartTutorialBtn.addEventListener('click', () => {
      initOnboarding(true);
    });
  }

  new AniTopApp();
}

// Entry point
if (isBrowser) {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBrowser);
  } else {
    initBrowser();
  }
}

// Export for module environments
export { AniTopApp };
